import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";

import { InternalButton, BackButton } from "@sharedComponents/buttons";

import debounce from "lodash/debounce";

import useRouter from "@stadium/hooks/lib/use-custom-route";
import Loaders from "@stadium/ui/src/components/loaders/loaders";
import { useWithAnalytics } from "@stadium/services/analytics/hooks/use-with-analytics";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useCreateSwagContext } from "@contexts/create-swag-context";

import { getCountry } from "@selectors/country";
import {
  getStoreCloneId,
  getStoreID,
  getStoreParentId,
  getStoreSyncedToES,
  getCommonElasticSearchStoreVariables,
  getIsBundleShop,
  getIsLockerShop,
  getIsLegacyLockerShop,
  getStoreCacheKey,
} from "@selectors/store";
import { getMenuAllChildren } from "@selectors/menu";
import { getPricelessProductFetchVariables } from "@selectors/product";
import { getAllMenuTaxons } from "@helpers/menu";
import { getCommonGlobalCountryVariables, getLockerShopESQueryVars } from "@helpers/store";

import { usePreviousRouteContext } from "@stadium/hooks/lib/use-previous-route";
import useFetchBaseProductList from "@hooks/search/use-fetch-base-product-list";
import { useStoreContext } from "@components/store/provider";
import { RELEVANCE_SORT_ITEM } from "@constants/products/listing";
import SearchedProducts from "@sharedComponents/global-new-search/searched-products";
import SearchedBrand from "@sharedComponents/global-new-search/searched-brand";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import {
  getMenuRoute,
  SEARCH_PAGE_PATHNAME,
  MENU_PAGE_PATHNAME,
  MENU_PAGE_PATHNAME_OLD,
  PRODUCT_VIEW_PATHNAME,
  STORE_INVITE_PAGE_PATHNAME,
  getSearchItemsRoute,
  SEARCH_QUERY_PAGE_PATHNAME,
  ALL_PRODUCTS_PATHNAME,
  STORE_HOME_PAGE_PATHNAME,
} from "@constants/routes";
import {
  OLD_STORE_HOME_PAGE_PATHNAME,
  OLD_MENU_PAGE_PATHNAME,
  OLD_MENU_PAGE_PATHNAME_WITH_SUBCATEGORY,
  OLD_ALL_PRODUCTS_PATHNAME,
  OLD_SEARCH_PAGE_PATHNAME,
  OLD_SEARCH_QUERY_PAGE_PATHNAME,
  OLD_PRODUCT_VIEW_PATHNAME,
  OLD_STORE_INVITE_PAGE_PATHNAME,
} from "@constants/old-routes";
import {
  ANALYTICS_EVENT_ACTION_CLICKED,
  ANALYTICS_EVENT_ACTION_PERFORMED,
} from "@stadium/services/analytics/constants/default";
import { HEADER_ANALYTICS_TARGET } from "@stadium/shared/src/constants/analytics/header";

import defaultStyles from "./main.module.scss";

const DEFAULT_CURRENT_PAGE = 1;
const ITEMS_PER_PAGE = 10;

const ALLOWED_BACK_BUTTON_ROUTES = [
  ALL_PRODUCTS_PATHNAME,
  STORE_HOME_PAGE_PATHNAME,
  MENU_PAGE_PATHNAME,
  MENU_PAGE_PATHNAME_OLD,
  OLD_STORE_HOME_PAGE_PATHNAME,
  OLD_MENU_PAGE_PATHNAME,
  OLD_MENU_PAGE_PATHNAME_WITH_SUBCATEGORY,
  OLD_ALL_PRODUCTS_PATHNAME,
];
const SKIPPED_BACK_BUTTON_ROUTES = [
  SEARCH_PAGE_PATHNAME,
  SEARCH_QUERY_PAGE_PATHNAME,
  PRODUCT_VIEW_PATHNAME,
  STORE_INVITE_PAGE_PATHNAME,
  OLD_SEARCH_PAGE_PATHNAME,
  OLD_SEARCH_QUERY_PAGE_PATHNAME,
  OLD_PRODUCT_VIEW_PATHNAME,
  OLD_STORE_INVITE_PAGE_PATHNAME,
];

const GlobalProductSearch = ({
  isCatalogSearchPage = false,
  styles = defaultStyles,
  handleClick,
  focusInputOnMount = false,
  searchBlockClass = "",
  searchInputClass = "",
  setIsSearchToggleActive = () => {},
}) => {
  const { getPreviousUrl } = usePreviousRouteContext();

  const showBackButton = Boolean(getPreviousUrl()?.pathname);

  const router = useRouter();
  const { searchQuery } = router.query;

  const [isSearchActive, setIsSearchActive] = useState(false);
  const withAnalytics = useWithAnalytics();

  const trackSearchEvent = useCallback(
    (target, action, eventProperties = {}) => {
      withAnalytics(undefined, { track: true, target, action, eventProperties })();
    },
    [withAnalytics],
  );

  const { onlyHasAllProductsMenu, storeBasePath, isGlobalCountry, isPreviewCatalogMode } =
    useStoreContext();
  const storeParentId = useSelector(getStoreParentId);
  const storeId = useSelector(getStoreID);
  const country = useSelector(getCountry);
  const syncedToEs = useSelector(getStoreSyncedToES);
  const storeCloneId = useSelector(getStoreCloneId);
  const allChildren = useSelector(getMenuAllChildren);
  const commonESVariables = useSelector(getCommonElasticSearchStoreVariables);
  const isBundleShop = useSelector(getIsBundleShop);
  const isLockerShop = useSelector(getIsLockerShop);
  const isLegacyLockerShop = useSelector(getIsLegacyLockerShop);
  const pricelessVariables = useSelector(getPricelessProductFetchVariables);
  const storeCacheKey = useSelector(getStoreCacheKey);
  const inputRef = useRef(null);

  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const menuRoute = getMenuRoute({ storeBasePath });

  const { getStoreUpdatedAt, previewMode } = usePreviewModeContext();
  const { isSwagCreationShopFlow, preferredColorNames, currentUserConfig } = useCreateSwagContext();
  const { isDesigner = false } = currentUserConfig || {};

  const [searchValue, setSearchValue] = useState(
    !focusInputOnMount && searchQuery ? decodeURIComponent(searchQuery) : "",
  );

  const { allMenuTaxonIds: taxonIds, anyMenuWithSwag } = useMemo(
    () => getAllMenuTaxons(allChildren),
    [allChildren, country],
  );

  useEffect(() => {
    if (focusInputOnMount && inputRef.current) {
      inputRef.current.focus();
    }
  }, [focusInputOnMount]);

  const handleSearchChange = (event) => {
    const { value } = event.target;
    setSearchValue(value);
    setIsSearchActive(Boolean(value));
  };

  const commonGlobalCountryVariables = getCommonGlobalCountryVariables({
    country,
    isGlobalCountry,
    countryKeyAttr: "countryCode",
  });

  const queryVariables = useMemo(() => {
    const variables = {
      storeId: storeParentId,
      designedStoreIds: [storeId],
      taxonIds,
      page: DEFAULT_CURRENT_PAGE,
      perPage: ITEMS_PER_PAGE,
      searchQuery: searchValue,
      skipWarehouse: !syncedToEs && !!storeCloneId,
      sortBy: RELEVANCE_SORT_ITEM.id,
      withSwag: anyMenuWithSwag,
      storeUpdatedAt: previewMode ? getStoreUpdatedAt() : storeCacheKey,
      optimizedSearch: isLockerShop,
      includeProductVariants: isBundleShop,
      showVariantsAsProducts: true,
      ...(isBundleShop && { bulkAvailable: false, withOutOfStock: true, withSwag: true }),
      ...commonESVariables,
      ...commonGlobalCountryVariables,
      ...getLockerShopESQueryVars({
        isLockerShop: isLegacyLockerShop,
      }),
      ...pricelessVariables,
    };

    if (isSwagCreationShopFlow) {
      variables.color = preferredColorNames?.map(({ presentation }) => presentation) || [];
      variables.countryCode = "";
      variables.includeNonColorProducts = true;
      variables.designer = isDesigner;
    }

    return variables;
  }, [
    isBundleShop,
    storeParentId,
    storeId,
    country,
    searchValue,
    taxonIds,
    commonESVariables,
    getStoreUpdatedAt,
    pricelessVariables,
    storeCacheKey,
    isSwagCreationShopFlow,
    preferredColorNames,
    isDesigner,
  ]);

  useEffect(() => {
    if (!onlyHasAllProductsMenu && searchValue) fetchSearchResults(queryVariables);
  }, [onlyHasAllProductsMenu, searchValue, country, taxonIds]);

  const {
    fetchedProducts,
    getProductList,
    updatedAggregations: aggregations,
    hasDataFetched,
  } = useFetchBaseProductList();
  const searchedBrands = aggregations?.searchedByBrandName;
  const showBrandsSuggestion = searchedBrands?.length > 0;

  const fetchSearchResults = useCallback(
    // eslint-disable-next-line react-hooks/use-memo
    debounce((variables) => {
      getProductList({
        variables,
      });
    }, 1000),
    [getProductList],
  );

  const redirectToResults = () => {
    if (!searchValue) return;
    setIsSearchActive(false);
    handleClick?.();
    trackSearchEvent(HEADER_ANALYTICS_TARGET.SEARCH, ANALYTICS_EVENT_ACTION_PERFORMED, {
      search_query: searchValue,
    });
    const pathname = getSearchItemsRoute({
      storeBasePath,
      searchQuery: encodeURIComponent(searchValue),
    });
    if (searchValue !== searchQuery) router.push({ pathname });

    document.getElementById("global-search").blur();
  };

  const handleEnterKeyPress = (event) => {
    if (event.keyCode === 13) redirectToResults();
  };

  const noDataView = () => <div className={styles.noData}>No matching results found.</div>;

  const renderViewAllProducts = () => {
    if (fetchedProducts.length < 2) return null;
    return (
      <div className={styles.viewAllBlock}>
        <InternalButton onClick={redirectToResults}>View all results</InternalButton>
      </div>
    );
  };

  const renderBrandsResults = () => {
    const searchedBrands = aggregations?.searchedByBrandName;
    if (searchedBrands?.length > 0)
      return (
        <>
          <div className={styles.searchTopHead}>Brands</div>
          {searchedBrands.map((brand) => (
            <SearchedBrand data={brand} key={brand.id} styles={styles} handleClick={handleClick} />
          ))}
        </>
      );
  };

  const renderProductList = () => {
    if (!searchValue) return null;
    if (!onlyHasAllProductsMenu && !hasDataFetched) return <Loaders.Spinner />;
    if (onlyHasAllProductsMenu || !fetchedProducts.length) return noDataView();
    const filteredProducts = showBrandsSuggestion ? fetchedProducts.slice(0, 3) : fetchedProducts;
    return (
      <>
        <div className={styles.searchTopHead} tabIndex={0}>
          Top Suggestions
        </div>
        {filteredProducts.map((product) => (
          <SearchedProducts
            data={product}
            key={product.id}
            setSearchValue={setSearchValue}
            styles={styles}
            handleClick={handleClick}
          />
        ))}
        {renderBrandsResults()}
        {renderViewAllProducts()}
      </>
    );
  };

  const className = classNames(styles.searchBlock, {
    [searchBlockClass]: Boolean(searchBlockClass),
    [styles.previewModeMobileSearchPage]: isCatalogSearchPage,
    [styles.catalogSearchBlock]: isPreviewCatalogMode,
    [styles.searchActive]: isSearchActive,
  });

  const renderGlobalSearch = () => {
    const hideSearchIcon = !isSearchActive && searchQuery;

    return (
      <div className={className}>
        {isMobileDevice && showBackButton && (
          <BackButton
            allowedRoutes={ALLOWED_BACK_BUTTON_ROUTES}
            skipRoutes={SKIPPED_BACK_BUTTON_ROUTES}
            appendQueryParams
            fallbackRoute={menuRoute}
            className={styles.backButton}
          >
            {""}
          </BackButton>
        )}
        <div className={styles.searchInputGroup}>
          <label>
            <input
              ref={inputRef}
              id="global-search"
              type="text"
              value={searchValue}
              className={classNames(
                { [styles.previewCatalogSearchBar]: isPreviewCatalogMode },
                searchInputClass,
              )}
              onChange={handleSearchChange}
              placeholder="Search all products"
              onKeyUp={handleEnterKeyPress}
              onSubmit={redirectToResults}
              autoComplete="off"
              onFocus={() => {
                setIsSearchToggleActive(true);
                trackSearchEvent(HEADER_ANALYTICS_TARGET.SEARCH, ANALYTICS_EVENT_ACTION_CLICKED);
              }}
              onBlur={() => {
                if (!searchValue) {
                  setIsSearchToggleActive(false);
                }
              }}
            />{" "}
            <button
              className={`${
                isSearchActive && !isPreviewCatalogMode ? styles.overlay : styles.hideOverlay
              }`}
              onClick={() => {
                setIsSearchActive(false);
              }}
              aria-hidden="true"
              tabIndex={-1}
            />
          </label>
          <button
            onClick={redirectToResults}
            className={`${styles.iconSearch} ${hideSearchIcon ? styles.hideIcon : ""}`}
            aria-label="Search"
          />
          {hideSearchIcon && (
            <BackButton
              allowedRoutes={ALLOWED_BACK_BUTTON_ROUTES}
              skipRoutes={SKIPPED_BACK_BUTTON_ROUTES}
              appendQueryParams
              fallbackRoute={menuRoute}
              icon={false}
              className={styles.backButtonWithClose}
              aria-label="Remove"
            >
              <span className={styles.iconClose} />
            </BackButton>
          )}
        </div>

        <div className={styles.searchDropdown} onClick={() => setIsSearchActive(false)}>
          {renderProductList()}
        </div>
      </div>
    );
  };

  return renderGlobalSearch();
};

export default GlobalProductSearch;
